<template>
  <div class="app flex-row align-items-center">
    <div class="container">
      <b-row class="justify-content-center">
        <b-col md="8">
          <b-card-group>
            <b-card no-body class="p-3">
              <b-card-body>
                <b-form @submit.prevent="onSubmit">
                  <div>
                    <h1>{{title}}</h1>
                    <p class="text-muted text-right">{{version}}  produced by OPTAGE</p>
                    <b-input-group class="mb-3">
                      <b-input-group-prepend><b-input-group-text><font-awesome-icon :icon="['far', 'user']" fixedWidth/></b-input-group-text></b-input-group-prepend>
                      <b-form-input type="text" class="form-control" v-model="form.userId" placeholder="ユーザーID" autocomplete="username email" required :disabled="isLoading || passcodeRequired"/>
                    </b-input-group>
                    <b-input-group class="mb-3">
                      <b-input-group-prepend><b-input-group-text><font-awesome-icon :icon="['fas', 'lock']" fixedWidth/></b-input-group-text></b-input-group-prepend>
                      <b-form-input type="password" class="form-control" v-model="form.password" placeholder="パスワード" autocomplete="current-password" required :disabled="isLoading || passcodeRequired"/>
                    </b-input-group>
                  </div>
                  <div v-if="passcodeRequired">
                  <!-- <div> -->
                    <!-- 2段階認証用フォーム -->
                    <b-input-group class="mb-3">
                      <b-input-group-prepend><b-input-group-text><font-awesome-icon :icon="['fas', 'user-shield']" fixedWidth/></b-input-group-text></b-input-group-prepend>
                      <b-form-input type="text" class="form-control" v-model="form.passcode" placeholder="認証コード" autocomplete="one-time-code" :disabled="isLoading" autocapitalize="off" inputmode="numeric" maxlength="6"/>
                    </b-input-group>
                    <!-- /2段階認証用フォーム -->
                  </div>
                  <b-row>
                    <b-col cols="6">
                      <b-button type="submit" variant="primary" class="loginButton px-3" :disabled="isLoading">ログイン&nbsp;<b-spinner small v-show="isLoading"/></b-button>
                    </b-col>
                    <b-col cols="6" class="text-right">
                      <b-button v-if="!passcodeRequired" variant="link" class="px-0" @click="showForgotPasswordModal()" :disabled="isLoading"><font-awesome-icon :icon="['far', 'question-circle']"/>パスワード不明</b-button>
                      <b-button v-else variant="link" class="px-0 " @click="showInfoOfPasscode()" :disabled="isLoading"><font-awesome-icon :icon="['far', 'question-circle']"/>認証コードとは</b-button>
                    </b-col>
                  </b-row>
                </b-form>
              </b-card-body>
            </b-card>
            <b-card no-body class="text-white bg-gray-700 d-md-down-none" style="max-width: 20rem;">
              <img src="img/brand/logo-biz.png" class="logo" alt="OPTAGE for Business">
            </b-card>
          </b-card-group>
          <div class="text-muted text-center">Copyright &copy;&nbsp;<a href="http://optage.co.jp/" target="_blank">OPTAGE Inc.</a></div>
        </b-col>
      </b-row>
    </div>

    <!-- modal -->
    <b-modal
      v-model="modal.loginFailure"
      title="ログイン失敗" 
      header-bg-variant="danger"
      ok-variant="secondary"
      ok-only
      @ok="modal.loginFailure = false"
    >
      ユーザーID、またはパスワードが不正です。<br>
      またはアカウントがロックされている可能性があります。
    </b-modal>

    <!-- パスワード初期化モーダル -->
    <validation-observer v-slot="{ invalid }">
      <b-modal
        v-model="modal.forgotPassword"
        title="パスワード初期化"
        size="md"
        no-close-on-backdrop
      >
        <b-container fluid>
          <!-- ユーザーID -->
          <b-row class="mb-1">
            <b-col cols="5">ユーザーID</b-col>
            <b-col>
              <validation-provider
                v-slot="validationContext"
                rules="required"
                name="ユーザーID"
              >
                <b-form-input
                  id="loginId"
                  v-model="passResetForm.loginId"
                  :disabled="isInitialize"
                  :state="getValidationState(validationContext)"
                  autocomplete="off"
                >
                </b-form-input>
                <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </validation-provider>
            </b-col>
          </b-row>
          <!-- 登録メールアドレス -->
          <b-row>
            <b-col cols="5">登録メールアドレス</b-col>
            <b-col>
            <validation-provider
              v-slot="validationContext"
              rules="required|email"
              name="登録メールアドレス"
            >
              <b-form-input
                id="mailAddress"
                v-model="passResetForm.mailAddress"
                :disabled="isInitialize"
                :state="getValidationState(validationContext)"
                autocomplete="off"
                >
              </b-form-input>
              <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
            </validation-provider>
            </b-col>
          </b-row>
        </b-container>
        <template v-slot:modal-footer="{ ok }">
          <!-- モーダル.送信ボタン -->
          <b-button variant="primary" @click="onPasswordInitialization" :disabled="invalid||isInitialize">
            <span>送信</span>
            <b-spinner small v-show="isInitialize"/>
          </b-button>
        </template>
      </b-modal>
    </validation-observer>

    <!-- 初回ログインパスワード変更モーダル -->
    <validation-observer v-slot="{ invalid }">
      <b-modal
        v-model="modal.firstLogin"
        title="初期パスワード変更"
        hide-header-close
        no-close-on-esc
        no-close-on-backdrop
      >
        <validation-provider
          v-slot="validationContext"
          rules="required|password"
          name="変更前パスワード"
        >
          <b-row class="mb-1">
            <b-col cols="5">変更前パスワード</b-col>
            <b-col>
              <b-form-input
                v-model="changeInitialPasswordForm.passwordBefore"
                type="password"
                :disabled="isChange"
                :state="getValidationState(validationContext)"
                autocomplete="off"
                name="passwordBefore"
              >
              </b-form-input>
              <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
            </b-col>
          </b-row>
        </validation-provider>

        <validation-provider
          v-slot="validationContext"
          rules="required|password|correlation:passwordAfterVerification"
          vid="passwordBefore"
          name="変更後パスワード"
        >
          <b-row class="mb-1">
            <b-col cols="5">変更後パスワード</b-col>
            <b-col>
              <b-form-input
                v-model="changeInitialPasswordForm.passwordAfter"
                type="password"
                :disabled="isChange"
                :state="getValidationState(validationContext)"
                autocomplete="off"
              >
              </b-form-input>
              <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
            </b-col>
          </b-row>
        </validation-provider>

        <validation-provider
          v-slot="validationContext"
          rules="required|password|correlation:passwordBefore"
          vid="passwordAfterVerification"
          name="変更後パスワード(確認)"
        >
          <b-row class="mb-1">
            <b-col cols="5">変更後パスワード(確認)</b-col>
            <b-col>
              <b-form-input
                v-model="changeInitialPasswordForm.passwordAfterVerification"
                type="password"
                :disabled="isChange"
                :state="getValidationState(validationContext)"
                autocomplete="off"
              >
              </b-form-input>
              <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
            </b-col>
          </b-row>
        </validation-provider>

        <template v-slot:modal-footer="{ ok }">
          <b-button variant="primary" @click="onChangeInitialPassword()" :disabled="invalid||isChange">
            変更
          </b-button>
          <b-spinner small v-show="isChange"/>
        </template>
      </b-modal>
    </validation-observer>

    <b-modal
      v-model="modal.changePassFailure"
      title="パスワード変更失敗" 
      header-bg-variant="danger"
      ok-variant="secondary"
      ok-only
      @ok="modal.changePassFailure=false"
    >
      入力内容に誤りがあります。
    </b-modal>

    <b-modal
      v-model="modal.changePassSuccess"
      title="パスワード変更完了" 
      header-bg-variant="primary"
      ok-variant="secondary"
      ok-only
      @ok="loginDo()"
    >
      パスワードを更新しました。
    </b-modal>

    <b-modal
      v-model="modal.passInitializeFailure"
      title="パスワード初期化失敗" 
      header-bg-variant="danger"
      ok-variant="secondary"
      ok-only
      @ok="modal.passInitializeFailure=false"
    >
      入力内容に誤りがあります。
    </b-modal>

    <b-modal
      v-model="modal.passInitializeSuccess"
      title="パスワード初期化完了" 
      header-bg-variant="primary"
      ok-variant="secondary"
      ok-only
      @ok="modal.passInitializeSuccess=false;modal.forgotPassword=false;"
    >
      仮パスワード発行を発行し、メールを送信しました。</br>
      メールが送信されない場合は、入力内容をご確認ください。
    </b-modal>

    <b-modal
      v-model="modal.infoOfPasscode"
      title="認証コードとは"
      header-bg-variant="primary"
      ok-only
      ok-variant="secondary"
      @ok="modal.infoOfPasscode=false"
    >
      2段階認証有効化の際にお選びいただいた認証方法（認証アプリまたはメール）に応じて通知される6桁の数字です。<br/>
      <div class="text-danger">
        <span class="font-weight-bold">【ご注意】</span><br/>
        <ul>
          <li>認証コードは有効期限内に入力してください。</li>
          <li>メールにて通知された認証コードの再通知をご希望の場合、再度ログインを行ってください。</li>
        </ul>
      </div>
    </b-modal>
    
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import FingerprintJS from '@fingerprintjs/fingerprintjs'

const WINDOW_FEATURES = "menubar=no,location=no,resizable=yes,scrollbars=yes,status=no"
const fpPromise = FingerprintJS.load()

export default {
  name: 'Login',
  data() {
    return {
      form: {
        userId: '',
        password: '',
        passscode: '',
      },
      modal: {
        forgotPassword: false,
        loginFailure: false,
        firstLogin: false,
        changePassFailure: false,
        changePassSuccess: false,
        passInitializeSuccess: false,
        passInitializeFailure: false,
        infoOfPasscode: false,
      },
      isLoading: false,
      isChange: false,
      isInitialize: false,
      passcodeRequired: false,
      bfp: '',
      passResetForm: {
        loginId: '',
        mailAddress: '',
      },
      changeInitialPasswordForm: {
        passwordBefore: '',
        passwordAfter: '',
        passwordAfterVerification: '',
        userId: ''
      },
    }
  },
  methods: {
    ...mapActions('auth', ['login']),
    ...mapActions('password', ['changeInitialPassword','passwordInitialization']),
    
    async onSubmit() {
      this.isLoading = true
      const params = {
        userId: this.form.userId,
        password: this.form.password,
        passcode: this.form.passcode,
        bfp: this.bfp
      }

      const returnCode = await this.login(params)
      if (returnCode === 0) {
        await this.safieAuthPageOpen()
        this.$router.push({name: '通知・発報履歴一覧'})
      } else if (returnCode === 2) {
        // 初期パスワード時
        
        this.showFirstLoginModal(this.form.userId)
      } else if (returnCode === 3) {
        // 2FA要求
        this.passcodeRequired = true
      } else {
        // エラーダイアログ表示
        this.modal.loginFailure = true
      }

      this.isLoading = false
    },

    getValidationState: ({dirty, validated, valid = null}) => dirty || validated ? valid : null,

    // 初期パスワード変更モーダル初期化
    showFirstLoginModal(userId) {
      this.changeInitialPasswordForm.passwordBefore = '' 
      this.changeInitialPasswordForm.passwordAfter = '' 
      this.changeInitialPasswordForm.passwordAfterVerification = '' 
      this.changeInitialPasswordForm.userId = userId
      this.modal.firstLogin = true
    },

    // 初期パスワード変更ボタン押下
    async onChangeInitialPassword() {
      this.isChange = true
      
      const resultCode = await this.changeInitialPassword({
        userId: this.changeInitialPasswordForm.userId,
        passwordBefore: this.changeInitialPasswordForm.passwordBefore,
        passwordAfter: this.changeInitialPasswordForm.passwordAfter
      })

      if (resultCode === 0) {
        this.modal.changePassSuccess = true
      } else {
        // エラーダイアログ表示
        this.modal.changePassFailure = true
      }

      this.isChange = false
    },
    // パスワード初期化処理モーダルの初期化
    showForgotPasswordModal() {
      this.passResetForm.loginId = ''
      this.passResetForm.mailAddress = ''
      this.modal.forgotPassword = true
    },
    // パスワード初期化 送信ボタン押下
    async onPasswordInitialization() {
      this.isInitialize = true
      await this.passwordInitialization({
        userId: this.passResetForm.loginId,
        mailAddress: this.passResetForm.mailAddress,
      })
      this.isInitialize = false
      this.modal.forgotPassword = false
      this.modal.passInitializeSuccess = true
    },
    // 初期パスワード完了後の処理
    async loginDo() {
      this.modal.changePassSuccess = false
      this.modal.firstLogin = false
      await this.safieAuthPageOpen()
      this.$router.push({name: '通知・発報履歴一覧'})
    },
    async safieAuthPageOpen() {
      if (!this.safieAuthenticationFlag()) return
      const authenticationUri = process.env.VUE_APP_SAFIE_API_AUTHENTICATION_URI
      const clientId = '&client_id=' + this.safieClientId()
      const state = '&state=' + this.clientId
      const redirectUri = '&redirect_uri=' + String(process.env.VUE_APP_SAFIE_REDIRECT_URI)
      await window.open(authenticationUri + redirectUri + state + clientId, 'safie_authentication', WINDOW_FEATURES)
    },
    showInfoOfPasscode() {
      this.modal.infoOfPasscode = true
    }
  },
  computed: {
    ...mapGetters('auth', ['idToken', 'refreshToken', 'clientId', 'safieClientId', 'safieAuthenticationFlag']),
    title: () => process.env.VUE_APP_TITLE,
    version: () => process.env.VUE_APP_VERSION,
  },
  async created() {
    // ブラウザフィンガープリント取得（2FA用）
    const fp = await fpPromise
    this.bfp = (await fp.get()).visitorId
    console.log(this.bfp)
  }
}
</script>

<style scoped>
  .loginButton {
    width: 120px;
  }
  .logo {
    display: block;
    position: absolute;
    width: 70%;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
  }
</style>
